import React from "react";
import { Eye } from "lucide-react";
import UserStatusBadge from "../UserStatusBadge/UserStatusBadge";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";

/* =======================
   Styles
======================= */
import styles from "./APITable.module.scss";

const APITable = ({
  columns = [],
  data = [],
  loading = false,
  title = "",
  renderCell,
  rowClassName,
}) => {
  return (
    <div className={styles["admin-api-table__wrapper"]}>
      {title && <h3 className={styles["admin-api-table__title"]}>{title}</h3>}

      <div className={styles["admin-api-table__container"]}>
        <table className={styles["admin-api-table"]}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={styles["admin-api-table__th"]}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ================= LOADING SKELETON ================= */}
            {loading ? (
              [...Array(6)].map((_, rowIndex) => (
                <tr key={rowIndex} className={styles["admin-api-table__row"]}>
                  {columns.map((col) => (
                    <td key={col.key} className={styles["admin-api-table__td"]}>
                      <div
                        className={styles["admin-api-table__skeleton"]}
                      ></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              /* ================= NO DATA ================= */
              <tr>
                <td
                  colSpan={columns.length}
                  className={styles["admin-api-table__no-data"]}
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              /* ================= ACTUAL DATA ================= */
              data.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className={`${styles["admin-api-table__row"]} ${
                    rowClassName ? rowClassName(row) : ""
                  }`}
                >
                  {columns.map((col) => {
                    const cellKey = `${row.id || rowIndex}-${col.key}`;

                    const customCell = renderCell?.(row, col.key);
                    if (customCell) {
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          {customCell}
                        </td>
                      );
                    }

                    if (col.render) {
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          {col.render(row)}
                        </td>
                      );
                    }

                    const value = row[col.key];

                    /* ===== NAME COLUMN ===== */
                    if (col.key === "name") {
                      if (row.avatar || row.avatarText || row.name) {
                        const initials =
                          row.avatarText ||
                          (row.name
                            ? row.name.substring(0, 2).toUpperCase()
                            : "?");
                        return (
                          <td
                            key={cellKey}
                            className={styles["admin-api-table__td"]}
                          >
                            <div
                              className={styles["admin-api-table__name-cell"]}
                            >
                              <span
                                className={
                                  styles["admin-api-table__name-avatar"]
                                }
                              >
                                {initials}
                              </span>
                              <span>{row.name}</span>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          {row.name}
                        </td>
                      );
                    }

                    /* ===== ERROR CODE / RATE ===== */
                    if (col.key === "errorCode" || col.key === "errorRate") {
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          <span
                            className={styles["admin-api-table__error-pill"]}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }

                    /* ===== STATUS BADGE ===== */
                    if (col.key === "status") {
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          <UserStatusBadge status={value} />
                        </td>
                      );
                    }

                    /* ===== ACTION COLUMN ===== */
                    if (col.key === "action") {
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          <div
                            className={styles["admin-api-table__action-row"]}
                          >
                            {row.onView && (
                              <Eye
                                size={20}
                                className={styles["admin-api-table__view-icon"]}
                                onClick={row.onView}
                              />
                            )}

                            {row.onToggle && (
                              <ToggleSwitch
                                isOn={row.active}
                                onToggle={row.onToggle}
                              />
                            )}
                          </div>
                        </td>
                      );
                    }

                    /* ===== KEY BADGE ===== */
                    if (col.key === "keyname") {
                      const displayValue = row.keyname || row.prekeyname;
                      const badgeClass = row.keyname
                        ? styles["admin-api-table__key-badge--production"]
                        : styles["admin-api-table__key-badge--unproduction"];

                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          <span
                            className={`${styles["admin-api-table__key-badge"]} ${badgeClass}`}
                          >
                            {displayValue}
                          </span>
                        </td>
                      );
                    }

                    /* ===== DATETIME ===== */
                    if (col.key === "datetime" && value) {
                      const parts = value.split(" ");
                      const date = parts[0];
                      const time = parts.slice(1).join(" ");
                      return (
                        <td
                          key={cellKey}
                          className={styles["admin-api-table__td"]}
                        >
                          {date}{" "}
                          <span
                            className={styles["admin-api-table__time-color"]}
                          >
                            {time}
                          </span>
                        </td>
                      );
                    }

                    /* ===== DEFAULT ===== */
                    return (
                      <td
                        key={cellKey}
                        className={styles["admin-api-table__td"]}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default APITable;
